// Micro Goals Widget - Daily objectives tracker
import { useState, useEffect, memo } from 'react';
import { Progress } from '@/components/ui/progress';
import { Check, Footprints, Droplets, Utensils, Dumbbell, Scale } from 'lucide-react';
import { celebrateAchievement } from '@/utils/celebrations';

const MicroGoals = memo(function MicroGoals({ 
  steps = 0, 
  stepGoal = 10000,
  water = 0,
  waterGoal = 8,
  meals = 0,
  mealGoal = 3,
  workout = false,
  weighed = false,
  language = 'fr'
}) {
  const [celebrated, setCelebrated] = useState({});
  
  const goals = [
    {
      id: 'steps',
      icon: Footprints,
      current: steps,
      target: stepGoal,
      label: language === 'fr' ? 'Pas' : 'Steps',
      color: 'text-green-500',
      bgColor: 'bg-green-500'
    },
    {
      id: 'water',
      icon: Droplets,
      current: water,
      target: waterGoal,
      label: language === 'fr' ? 'Verres d\'eau' : 'Glasses',
      color: 'text-blue-500',
      bgColor: 'bg-blue-500'
    },
    {
      id: 'meals',
      icon: Utensils,
      current: meals,
      target: mealGoal,
      label: language === 'fr' ? 'Repas' : 'Meals',
      color: 'text-orange-500',
      bgColor: 'bg-orange-500'
    },
    {
      id: 'workout',
      icon: Dumbbell,
      current: workout ? 1 : 0,
      target: 1,
      label: language === 'fr' ? 'Sport' : 'Workout',
      color: 'text-purple-500',
      bgColor: 'bg-purple-500'
    },
    {
      id: 'weight',
      icon: Scale,
      current: weighed ? 1 : 0,
      target: 1,
      label: language === 'fr' ? 'Pesée' : 'Weigh-in',
      color: 'text-pink-500',
      bgColor: 'bg-pink-500'
    }
  ];
  
  // Check for completed goals and celebrate
  useEffect(() => {
    goals.forEach(goal => {
      const isComplete = goal.current >= goal.target;
      if (isComplete && !celebrated[goal.id]) {
        setCelebrated(prev => ({ ...prev, [goal.id]: true }));
        // Small celebration for each goal
        setTimeout(() => celebrateAchievement('default'), 100);
      }
    });
  }, [steps, water, meals, workout, weighed]);
  
  const completedCount = goals.filter(g => g.current >= g.target).length;
  const totalProgress = Math.round((completedCount / goals.length) * 100);
  
  return (
    <div className="space-y-3">
      {/* Overall progress bar */}
      <div className="flex items-center gap-2">
        <Progress value={totalProgress} className="h-2 flex-1" />
        <span className="text-xs font-medium text-muted-foreground">
          {completedCount}/{goals.length}
        </span>
      </div>
      
      {/* Individual goals */}
      <div className="grid grid-cols-5 gap-2">
        {goals.map(goal => {
          const progress = Math.min((goal.current / goal.target) * 100, 100);
          const isComplete = progress >= 100;
          
          return (
            <div 
              key={goal.id}
              className={`relative flex flex-col items-center p-2 rounded-lg transition-all ${
                isComplete 
                  ? 'bg-gradient-to-b from-green-500/20 to-green-500/5 ring-1 ring-green-500/30' 
                  : 'bg-muted/30'
              }`}
            >
              <div className={`relative ${goal.color}`}>
                <goal.icon size={18} />
                {isComplete && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                    <Check size={8} className="text-white" />
                  </div>
                )}
              </div>
              
              <span className="text-[10px] mt-1 text-muted-foreground truncate w-full text-center">
                {goal.label}
              </span>
              
              {/* Mini progress ring */}
              <svg className="w-6 h-6 mt-1" viewBox="0 0 24 24">
                <circle
                  cx="12" cy="12" r="10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-muted/30"
                />
                <circle
                  cx="12" cy="12" r="10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeDasharray={`${progress * 0.628} 100`}
                  strokeLinecap="round"
                  className={isComplete ? 'text-green-500' : goal.color}
                  transform="rotate(-90 12 12)"
                />
              </svg>
              
              {/* Remaining text */}
              {!isComplete && goal.target > 1 && (
                <span className="text-[9px] text-muted-foreground">
                  {Math.max(0, goal.target - goal.current)} {language === 'fr' ? 'restant' : 'left'}
                </span>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Encouragement message */}
      {completedCount < goals.length && (
        <p className="text-xs text-center text-muted-foreground">
          {language === 'fr' 
            ? `Plus que ${goals.length - completedCount} objectif${goals.length - completedCount > 1 ? 's' : ''} !`
            : `${goals.length - completedCount} goal${goals.length - completedCount > 1 ? 's' : ''} to go!`
          }
        </p>
      )}
      
      {completedCount === goals.length && (
        <p className="text-xs text-center text-green-500 font-medium">
          🎉 {language === 'fr' ? 'Tous les objectifs atteints !' : 'All goals completed!'}
        </p>
      )}
    </div>
  );
});

export default MicroGoals;
